import { CdkConnectedOverlay, CdkOverlayOrigin } from "@angular/cdk/overlay";
import { NgClass, NgTemplateOutlet } from "@angular/common";
import { RouterLink } from "@angular/router";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
} from "@angular/core";
import {
  ClipboardService,
  IBadgeComponent,
  IButtonDirective,
  ICardComponent,
  ICardContent,
  ICheckboxComponent,
  ITooltipDirective,
  ToasterService,
  IIconComponent,
  SpinnerComponent,
} from "@front/m19-ui";
import { Marketplace, StrategyStateEnum } from "@front/m19-api-client";
import { ProductEx, StrategyEx, StrategyTypeStr } from "@front/m19-models";
import { AsinService } from "@front/m19-services";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ViewTypeEnum } from "@m19-board/strategies/strategy-list/strategy-list.component";
import { take } from "rxjs";
import { ProductThumbnailComponent } from "../../product-view/product-thumbnail.component";
import { StrategyStateComponent } from "@m19-board/shared/strategy-state/strategy-state.component";

@Component({
  selector: "app-asin-item",
  templateUrl: "./asin-item.component.html",
  imports: [
    ProductThumbnailComponent,
    SpinnerComponent,
    NgClass,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    ICardComponent,
    IButtonDirective,
    ITooltipDirective,
    RouterLink,
    NgTemplateOutlet,
    IBadgeComponent,
    ICheckboxComponent,
    TranslocoDirective,
    StrategyStateComponent,
    ICardContent,
    IIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AsinItemComponent implements OnInit {
  private translocoService = inject(TranslocoService);

  private asinService = inject(AsinService);
  private clipboard = inject(ClipboardService);
  private toasterService = inject(ToasterService);

  readonly ViewTypeEnum = ViewTypeEnum;

  @Input() set asin(asin: string) {
    this._asin = asin;
    if (this._marketplace) {
      this.setProduct();
    }
  }

  _asin!: string;

  @Input() set selected(v: boolean) {
    this._selected = v;
    this.cardStyle = this.computeStyle();
  }

  _selected!: boolean;

  @Input() set marketplace(m: Marketplace) {
    this._marketplace = m;
    if (this._asin) {
      this.setProduct();
    }
  }

  _marketplace!: Marketplace;

  @Input() isReadOnly = false;

  @Input() set isSelectable(v: boolean) {
    this._isSelectable = v;
    this.cardStyle = this.computeStyle();
  }

  _isSelectable = false;

  @Input() set disabled(v: boolean) {
    this._disabled = v;
    this.cardStyle = this.computeStyle();
  }

  _disabled = false;

  @Input() isLast = false;
  @Input() isIneligible = false;
  @Input() ineligibilityReason?: string;
  @Input() isPaused = false;
  @Input() actionsButtons = false;
  @Input() viewMode: ViewTypeEnum = ViewTypeEnum.LIST;

  @Input() set activeStrategies(s: StrategyEx[] | undefined) {
    this._activeStrategies.set(s?.filter((str) => str.state === StrategyStateEnum.ENABLED));
  }

  @Output() asinSelected = new EventEmitter<string>();

  product = signal<ProductEx | undefined>(undefined);
  _activeStrategies = signal<StrategyEx[] | undefined>(undefined);
  activeStrategiesMessage = computed(() => {
    if (this._activeStrategies()?.length === 0) return undefined;
    return this.translocoService.translate("asin-item.active_strategy", { count: this._activeStrategies()?.length });
  });

  cardStyle!: string;
  activeStrategiesOrigin?: CdkOverlayOrigin;
  overlayOpened = false;
  @ViewChild("activeStrategiesPopup") activeStrategiesPopup!: ElementRef;

  ngOnInit(): void {
    this.cardStyle = this.computeStyle();
  }

  private setProduct() {
    this.asinService
      .getProductWithMarketplace(this._asin, this._marketplace)
      .pipe(take(1))
      .subscribe((p: ProductEx) => {
        this.product.set(p);
      });
  }

  computeStyle() {
    let style = "relative group ";
    if (!this._disabled) style = style.concat(" ", "cursor-pointer duration-75 hover:bg-gray-100");
    else return style.concat(" ", "cursor-not-allowed select-none opacity-75");
    if (this._isSelectable && this._selected) {
      style = style.concat(" ", "ring-2 ring-main-300 !bg-main-50");
    }

    return style;
  }

  toggleAsin() {
    if (!this._isSelectable || this.isReadOnly || this._disabled) return;
    this._selected = !this._selected;
    this.asinSelected.emit(this._asin);
  }

  copyToClipboard(str: string | undefined) {
    this.clipboard.copyToClipboard(str);
  }

  displayActiveStrategies(trigger: CdkOverlayOrigin) {
    this.activeStrategiesOrigin = trigger;
    this.overlayOpened = true;
  }

  hideActiveStrategies() {
    this.activeStrategiesOrigin = undefined;
    this.overlayOpened = false;
  }

  protected readonly StrategyTypeStr = StrategyTypeStr;
}
